// @ts-nocheck
/* ============================================================
   "Mark the slabs" — concrete areas drawn on the satellite view
   (plan §5.1, roadmap §13 item 9).

   The fence estimator traces a line; this traces AREAS. Type the address, the
   lot and the house footprint come up (lib/parcel.ts, lib/buildings.ts — the
   same free sources the fence estimator uses), then tap the corners of each
   slab: a corner within a few feet of the house snaps to it, a near-right
   angle squares up, a rectangle tool follows the house's grid. Every slab
   shows its square feet as you draw; "Use these measurements" turns them into
   the estimator's blocks (lib/slabs.ts applySlabsToBlocks).

   ConcreteSitePlan is the printable plan: the slabs on the lot with the house
   as a landmark, for the quote page and the customer's hub (geometry only —
   lib/slabs.ts planOf, allowlisted by server/src/hubscope.js).
   ============================================================ */
import React from "react";
import { Icon } from "./registry";
import { ensureLeaflet, destroyMap } from "./lib/leaflet";
import { SLAB_KINDS, kindLabel, jobForKind, newSlab, withPoly, slabLabel, snapPoint, closesAt, axisOf, rectFromCorners, areaFt2, edgeFt, centroid, slabSummary, planOf } from "./lib/slabs";

const TILES = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const DEFAULT_CENTER: [number, number] = [44.5213, -88.0241];
const JOBS: [string, string][] = [["lift", "Lift & level"], ["coat", "Floor coating"], ["clean", "Cleaning"], ["seal", "Sealing"]];
const fps = (site: any): any[] => (site && Array.isArray(site.footprints) && site.footprints.length ? site.footprints : site && site.house ? [site.house] : []);
/* how close counts as "on it", in feet — generous when zoomed out, tight when zoomed in */
const tolFor = (m: any) => { const z = m && typeof m.getZoom === "function" ? m.getZoom() : 20; return Math.max(2, Math.min(10, 3 * Math.pow(2, 20 - z))); };
const num0 = (n: any) => Math.round(Number(n) || 0).toLocaleString("en-US");

export function SlabMap({ site, slabs, address, onChange, onApply }: any) {
  const list: any[] = Array.isArray(slabs) ? slabs : [];
  const mapEl = React.useRef<any>(null);
  const map = React.useRef<any>(null);
  const layers = React.useRef<any>(null);     // everything redrawn from state
  const preview = React.useRef<any>(null);    // the rubber band under the cursor
  const draggingRef = React.useRef<any>(false);
  const [tool, setTool] = React.useState<any>("trace");
  const [kind, setKind] = React.useState<any>("driveway");
  const [job, setJob] = React.useState<any>("");         // "" = by kind
  const [draft, setDraft] = React.useState<any[]>([]);
  const [rectA, setRectA] = React.useState<any>(null);
  const [sel, setSel] = React.useState<any>(null);
  const [busy, setBusy] = React.useState<any>(false);
  const [hint, setHint] = React.useState<any>("Type the address and press Find, then tap the corners of a slab.");
  const [ready, setReady] = React.useState<any>(false);
  const refs = React.useRef<any>({});
  refs.current = { tool, kind, job, draft, rectA, sel, site, slabs: list, onChange, onApply };

  const snapCtx = (extra: any = {}) => ({ footprints: fps(refs.current.site), slabs: refs.current.slabs, ...extra });
  const addSlab = (poly: any) => {
    const r = refs.current;
    const s = newSlab(poly, r.kind, r.job || undefined);
    r.onChange({ slabs: [...r.slabs, s] });
    setSel(s.id);
    setHint(`${slabLabel(s)} added. Trace the next one, or “Use these measurements”.`);
  };
  const finishDraft = () => {
    const d = refs.current.draft;
    if (d.length >= 3) addSlab(d);
    setDraft([]);
  };
  const onMapClick = (e: any) => {
    if (draggingRef.current) return;
    const m = map.current; const p = [e.latlng.lat, e.latlng.lng];
    const r = refs.current; const tol = tolFor(m);
    if (r.tool === "trace") {
      if (r.draft.length >= 3 && closesAt(p, r.draft[0], tol + 1)) { finishDraft(); return; }
      const prev = r.draft[r.draft.length - 1], prevPrev = r.draft[r.draft.length - 2];
      const snap = snapPoint(p, snapCtx({ draft: r.draft, prev, prevPrev, axisDeg: r.draft.length === 1 ? axisOf(fps(r.site), r.draft[0]) : undefined }), tol);
      const next = [...r.draft, snap.pt];
      setDraft(next);
      const why = snap.kind === "vertex" ? " · snapped to a corner" : snap.kind === "edge" ? " · snapped to a wall" : snap.kind === "ortho" ? " · squared" : "";
      setHint(next.length < 3 ? `${next.length} corner${next.length === 1 ? "" : "s"}${why}. Keep tapping; tap the first corner to close.` : `${num0(areaFt2(next))} sq ft so far${why}. Tap the first corner or press Enter to close.`);
      return;
    }
    if (r.tool === "rect") {
      const snap = snapPoint(p, snapCtx(), tol).pt;
      if (!r.rectA) { setRectA(snap); setHint("Now tap the opposite corner."); return; }
      addSlab(rectFromCorners(r.rectA, snap, axisOf(fps(r.site), r.rectA)));
      setRectA(null);
      return;
    }
    setSel(null);
  };
  const onMove = (e: any) => {
    const L = (window as any).L; const m = map.current; const r = refs.current;
    if (!L || !m || !preview.current) return;
    preview.current.clearLayers();
    const p = [e.latlng.lat, e.latlng.lng];
    if (r.tool === "trace" && r.draft.length) {
      const last = r.draft[r.draft.length - 1];
      L.polyline([last, p], { color: "#f5c451", weight: 1.5, dashArray: "4 4", interactive: false }).addTo(preview.current);
      const ft = edgeFt(last, p);
      L.marker(p, { icon: L.divIcon({ className: "cx-slab-label draft", html: `${Math.round(ft)}′`, iconSize: null }), interactive: false }).addTo(preview.current);
    } else if (r.tool === "rect" && r.rectA) {
      const poly = rectFromCorners(r.rectA, p, axisOf(fps(r.site), r.rectA));
      L.polygon(poly, { color: "#f5c451", weight: 1.5, dashArray: "4 4", fillOpacity: 0.08, interactive: false }).addTo(preview.current);
      const c = centroid(poly);
      if (c) L.marker(c, { icon: L.divIcon({ className: "cx-slab-label draft", html: `${num0(areaFt2(poly))} sq ft`, iconSize: null }), interactive: false }).addTo(preview.current);
    }
  };

  // the map, once
  React.useEffect(() => {
    let dead = false; let m: any = null; let ro: any = null;
    ensureLeaflet().then((L) => {
      if (dead || !mapEl.current) return;
      if (L.Draggable && L.Draggable.prototype && L.Draggable.prototype.options) L.Draggable.prototype.options.clickTolerance = 10;
      const c = site && Array.isArray(site.center) ? site.center : null;
      m = L.map(mapEl.current, { zoomControl: false, attributionControl: false }).setView(c || DEFAULT_CENTER, c ? 20 : 18);
      L.tileLayer(TILES, { maxZoom: 21, maxNativeZoom: 20 }).addTo(m);
      L.control.zoom({ position: "topright" }).addTo(m);
      layers.current = L.layerGroup().addTo(m);
      preview.current = L.layerGroup().addTo(m);
      m.on("click", onMapClick);
      m.on("mousemove", onMove);
      m.on("mouseout", () => preview.current && preview.current.clearLayers());
      mapEl.current.addEventListener("contextmenu", (e: any) => e.preventDefault());
      map.current = m;
      setTimeout(() => { try { m.invalidateSize(); } catch {} }, 60);
      if (typeof ResizeObserver !== "undefined") { ro = new ResizeObserver(() => { try { m.invalidateSize(); } catch {} }); ro.observe(mapEl.current); }
      setReady(true);
      if (list.length || (site && (site.house || site.parcel))) setTimeout(() => { if (!dead) try { fit(); } catch { /* unmounted */ } }, 120);
    });
    return () => { dead = true; if (ro) ro.disconnect(); if (m) destroyMap(m); if (map.current === m) map.current = null; };
  }, []);

  // everything drawn from state
  React.useEffect(() => {
    const L = (window as any).L; const m = map.current; const g = layers.current;
    if (!L || !m || !g || !ready) return;
    g.clearLayers();
    if (site && Array.isArray(site.parcel) && site.parcel.length >= 3) L.polygon(site.parcel, { color: "#9fb2c9", weight: 1, dashArray: "5 5", fill: false, interactive: false }).addTo(g);
    fps(site).forEach((f: any) => L.polygon(f, { color: "#f59e6b", weight: 1.5, dashArray: "4 3", fillColor: "#f59e6b", fillOpacity: 0.08, interactive: false }).addTo(g));
    list.forEach((s: any) => {
      const on = s.id === sel;
      const poly = L.polygon(s.poly, { color: on ? "#2ff3d6" : "#0fb39a", weight: on ? 3 : 2, fillColor: "#0fb39a", fillOpacity: on ? 0.3 : 0.18 }).addTo(g);
      poly.on("click", (e: any) => { if (refs.current.tool === "select") { L.DomEvent.stop(e); setSel(s.id); } });
      const c = centroid(s.poly);
      if (c) {
        const lbl = L.marker(c, { icon: L.divIcon({ className: "cx-slab-label" + (on ? " on" : ""), html: slabLabel(s), iconSize: null }), interactive: true, keyboard: false }).addTo(g);
        lbl.on("click", (e: any) => { L.DomEvent.stop(e); setSel(s.id); setTool("select"); });
      }
      if (on) {
        s.poly.forEach((v: any, i: number) => {
          const mk = L.marker(v, { draggable: true, keyboard: false, icon: L.divIcon({ className: "vtx-wrap", html: '<div class="vtx gold"></div>', iconSize: [30, 30] }) }).addTo(g);
          mk.on("dragstart", () => { draggingRef.current = true; });
          mk.on("drag", (e: any) => { const ll = e.target.getLatLng(); const pts = s.poly.map((q: any, k: number) => (k === i ? [ll.lat, ll.lng] : q)); poly.setLatLngs(pts); });
          mk.on("dragend", (e: any) => {
            const ll = e.target.getLatLng();
            const others = refs.current.slabs.filter((x: any) => x.id !== s.id);
            const snap = snapPoint([ll.lat, ll.lng], { footprints: fps(refs.current.site), slabs: others, prev: s.poly[(i + s.poly.length - 1) % s.poly.length], prevPrev: s.poly[(i + s.poly.length - 2) % s.poly.length] }, tolFor(m));
            const pts = s.poly.map((q: any, k: number) => (k === i ? snap.pt : q));
            refs.current.onChange({ slabs: refs.current.slabs.map((x: any) => (x.id === s.id ? withPoly(x, pts) : x)) });
            setTimeout(() => { draggingRef.current = false; }, 80);
          });
          mk.on("contextmenu", (e: any) => { L.DomEvent.stop(e); if (s.poly.length <= 3) return; refs.current.onChange({ slabs: refs.current.slabs.map((x: any) => (x.id === s.id ? withPoly(x, s.poly.filter((_: any, k: number) => k !== i)) : x)) }); });
        });
      }
    });
    if (draft.length) {
      if (draft.length >= 2) L.polyline(draft, { color: "#2ff3d6", weight: 2 }).addTo(g);
      if (draft.length >= 3) L.polygon(draft, { color: "#2ff3d6", weight: 1, dashArray: "3 3", fillColor: "#2ff3d6", fillOpacity: 0.12, interactive: false }).addTo(g);
      draft.forEach((v: any, i: number) => L.marker(v, { keyboard: false, icon: L.divIcon({ className: "vtx-wrap", html: `<div class="vtx${i === 0 ? " first" : ""}"></div>`, iconSize: [30, 30] }), interactive: i === 0 })
        .on("click", (e: any) => { if (i === 0 && refs.current.draft.length >= 3) { L.DomEvent.stop(e); finishDraft(); } }).addTo(g));
      if (draft.length >= 3) { const c = centroid(draft); if (c) L.marker(c, { icon: L.divIcon({ className: "cx-slab-label draft", html: `${num0(areaFt2(draft))} sq ft`, iconSize: null }), interactive: false }).addTo(g); }
    }
    if (rectA) L.marker(rectA, { keyboard: false, interactive: false, icon: L.divIcon({ className: "vtx-wrap", html: '<div class="vtx first"></div>', iconSize: [30, 30] }) }).addTo(g);
  }, [site, slabs, draft, rectA, sel, tool, ready]);

  function fit() {
    const L = (window as any).L; const m = map.current; if (!L || !m) return;
    const pts: any[] = [];
    refs.current.slabs.forEach((s: any) => pts.push(...s.poly));
    const st = refs.current.site;
    if (st && Array.isArray(st.house)) pts.push(...st.house);
    if (!pts.length && st && Array.isArray(st.parcel)) pts.push(...st.parcel);
    if (!pts.length && st && Array.isArray(st.center)) { m.setView(st.center, 20); return; }
    if (!pts.length) return;
    try { m.fitBounds(L.latLngBounds(pts), { padding: [40, 40], maxZoom: 20 }); } catch {}
  }

  /* the address → the lot and the house footprint (lib/parcel.ts, lib/buildings.ts) */
  async function find() {
    const P = (window as any).VFP_PARCEL, B = (window as any).VFP_BUILDINGS;
    const q = String(address || "").trim();
    if (!q || !P || !P.geocode) { setHint(q ? "Address lookup isn't available here." : "Type the job address first."); return; }
    setBusy(true); setHint("Finding the address…");
    try {
      const g = await P.geocode(q);
      if (!g) { setHint("Couldn't find that address — pan the map to the house and trace freely."); return; }
      const center = [g.lat, g.lng];
      let next: any = { ...(site || {}), center, address: g.address || q, house: null, footprints: [], parcel: null, source: null };
      onChange({ site: next, ...(g.address && !String(address || "").trim() ? { address: g.address } : {}) });
      if (map.current) map.current.setView(center, 20);
      setHint("Found it. Looking for the lot and the house…");
      let parcel: any = null;
      try { parcel = P.resolve ? await P.resolve(g.lat, g.lng, g.address || q) : null; } catch { parcel = null; }
      if (parcel && Array.isArray(parcel.polygon) && parcel.polygon.length >= 3 && !parcel.noCoverage) next = { ...next, parcel: parcel.polygon };
      let house: any = null;
      try { house = B && B.detectHouse && (next.parcel || parcel && parcel.polygon) ? await B.detectHouse(next.parcel || parcel.polygon) : null; } catch { house = null; }
      if (house && Array.isArray(house.polygon)) next = { ...next, house: house.polygon, footprints: Array.isArray(house.all) ? house.all : [house.polygon], source: house.source || null };
      onChange({ site: next });
      setHint(house ? `House footprint found (${house.source === "cv" ? "from the aerial" : house.source === "usa" ? "national footprints" : "OpenStreetMap"}) — corners snap to it. Tap the corners of the first slab.` : "No footprint on file for this lot — trace freely; near-right angles still square up.");
      setTimeout(() => fit(), 100);
    } finally { setBusy(false); }
  }

  const onKey = (e: any) => {
    if (e.key === "Enter") { if (refs.current.draft.length >= 3) { e.preventDefault(); finishDraft(); } }
    else if (e.key === "Escape") { setDraft([]); setRectA(null); setSel(null); }
    else if ((e.key === "Delete" || e.key === "Backspace") && refs.current.sel) { e.preventDefault(); removeSlab(refs.current.sel); }
    else if (e.key === "z" && (e.metaKey || e.ctrlKey) && refs.current.draft.length) { e.preventDefault(); setDraft((d) => d.slice(0, -1)); }
  };
  const removeSlab = (id: any) => { onChange({ slabs: list.filter((s: any) => s.id !== id) }); if (sel === id) setSel(null); };
  const patchSlab = (id: any, patch: any) => onChange({ slabs: list.map((s: any) => (s.id === id ? { ...s, ...patch } : s)) });
  const sum = slabSummary(list);
  const selected = list.find((s: any) => s.id === sel) || null;

  return (
    <div className="card pad" style={{ display: "grid", gap: 10 }}>
      <div className="row between" style={{ flexWrap: "wrap", gap: 8 }}>
        <div>
          <div className="kicker">Mark the slabs</div>
          <div className="muted" style={{ fontSize: 12.5 }}>{sum.count ? `${sum.count} slab${sum.count === 1 ? "" : "s"} · ${num0(sum.sqft)} sq ft${sum.sections ? ` · ${sum.sections} lift section${sum.sections === 1 ? "" : "s"}` : ""}` : "Tap the corners of each slab on the satellite view — square feet fill in as you draw."}</div>
        </div>
        <div className="row gap-8">
          <button type="button" className="btn btn-ghost btn-sm" onClick={fit} title="Fit the view to what's drawn"><Icon name="mappin" size={13} />Fit</button>
          {list.length > 0 && <button type="button" className="btn btn-primary btn-sm" onClick={() => onApply(list)}><Icon name="check" size={13} />Use these measurements</button>}
        </div>
      </div>
      <div className="row gap-8" style={{ flexWrap: "wrap" }}>
        <input className="input" style={{ flex: "1 1 260px" }} value={address || ""} placeholder="Job address" onChange={(e) => onChange({ address: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); find(); } }} />
        <button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={find}><Icon name="search" size={13} />{busy ? "Finding…" : "Find"}</button>
      </div>
      <div className="cx-map-tools">
        {[["trace", "Trace a slab"], ["rect", "Rectangle"], ["select", "Select / move"]].map(([id, label]) => (
          <button key={id} type="button" className={"filter-chip" + (tool === id ? " on" : "")} aria-pressed={tool === id} onClick={() => { setTool(id); setDraft([]); setRectA(null); if (id !== "select") setSel(null); setHint(id === "trace" ? "Tap the corners of the slab; tap the first corner to close." : id === "rect" ? "Tap one corner of the slab, then the opposite corner — it follows the house's grid." : "Tap a slab to select it; drag its corners, right-click a corner to remove it, Delete removes the slab."); }}>{label}</button>
        ))}
        <select className="select" style={{ width: "auto" }} value={kind} onChange={(e) => setKind(e.target.value)} title="What kind of concrete the next slab is">
          {SLAB_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
        </select>
        <select className="select" style={{ width: "auto" }} value={job} onChange={(e) => setJob(e.target.value)} title="Which job the next slab is drawn for">
          <option value="">{JOBS.find(([id]) => id === jobForKind(kind))?.[1] || "Lift & level"} (by kind)</option>
          {JOBS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
        {draft.length > 0 && <>
          <button type="button" className="btn btn-outline btn-sm" disabled={draft.length < 3} onClick={finishDraft}><Icon name="check" size={13} />Close slab</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDraft((d) => d.slice(0, -1))}><Icon name="undo" size={13} />Undo corner</button>
        </>}
        {selected && <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeSlab(selected.id)} style={{ color: "var(--red)" }}><Icon name="trash" size={13} />Delete slab</button>}
      </div>
      <div className="cx-map" tabIndex={0} onKeyDown={onKey}>
        <div className="leaflet-host" ref={mapEl} />
        <div className="cx-map-hint">{hint}</div>
      </div>
      {list.length > 0 && (
        <div style={{ display: "grid", gap: 6 }}>
          {list.map((s: any) => (
            <div key={s.id} className={"cx-slab-row" + (s.id === sel ? " on" : "")} onClick={() => { setSel(s.id); setTool("select"); const c = centroid(s.poly); if (c && map.current) map.current.panTo(c); }}>
              <span style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>{slabLabel(s)}</span>
              <select className="select" style={{ width: "auto", fontSize: 12 }} value={s.kind} onClick={(e) => e.stopPropagation()} onChange={(e) => patchSlab(s.id, { kind: e.target.value, jobType: s.jobType === jobForKind(s.kind) ? jobForKind(e.target.value) : s.jobType })}>
                {SLAB_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
              <select className="select" style={{ width: "auto", fontSize: 12 }} value={s.jobType} onClick={(e) => e.stopPropagation()} onChange={(e) => patchSlab(s.id, { jobType: e.target.value })}>
                {JOBS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
              <button type="button" className="btn btn-ghost btn-sm" style={{ padding: "2px 6px" }} onClick={(e) => { e.stopPropagation(); removeSlab(s.id); }} title="Remove this slab"><Icon name="trash" size={12} /></button>
            </div>
          ))}
          <p className="faint" style={{ fontSize: 11, margin: 0 }}>“Use these measurements” fills each job type's area, sections and surfaces from the slabs — anything else you typed (drop, cracks, finish) stays. The plan prints on the quote and shows on the customer's page.</p>
        </div>
      )}
    </div>
  );
}

/* The printable plan: the slabs on the lot, the house as a landmark. `plan` is
   lib/slabs.ts planOf (or the hub's allowlisted copy of it). North up, a 20 ft
   scale bar, each slab labelled with what it is and its square feet. */
export function ConcreteSitePlan({ plan, kicker, kickerStyle }: any) {
  const p = plan && Array.isArray(plan.slabs) && plan.slabs.length ? plan : null;
  if (!p) return null;
  const frame: any[] = [];
  p.slabs.forEach((s: any) => frame.push(...s.poly));
  if (Array.isArray(p.house) && p.house.length >= 3) frame.push(...p.house);
  if (frame.length < 3) return null;
  const k = Math.cos(frame[0][0] * Math.PI / 180);
  const pj = (pt: any) => [pt[1] * k * 111320, -pt[0] * 110540];
  const fp = frame.map(pj);
  const xs = fp.map((q) => q[0]), ys = fp.map((q) => q[1]);
  const minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys), maxy = Math.max(...ys);
  const w = (maxx - minx) || 1, h = (maxy - miny) || 1, pad = 0.12 * Math.max(w, h) + 3;
  const VW = 620, sc = VW / (w + 2 * pad), VH = Math.max(220, Math.round((h + 2 * pad) * sc));
  const TX = (x: number) => (x - minx + pad) * sc, TY = (y: number) => (y - miny + pad) * sc;
  const path = (ring: any[]) => ring.map(pj).map((q) => [TX(q[0]), TY(q[1])]).map((q) => q.join(",")).join(" ");
  const ctr = (ring: any[]) => { const c = centroid(ring); const q = pj(c); return [TX(q[0]), TY(q[1])]; };
  const barPx = 20 * 0.3048 * sc;   // 20 ft
  const footprints = (Array.isArray(p.footprints) && p.footprints.length ? p.footprints : p.house ? [p.house] : []).filter((r: any) => Array.isArray(r) && r.length >= 3);
  return (
    <div className="cx-plan">
      {kicker && <div className="kicker" style={kickerStyle || { marginBottom: 8 }}>{kicker}</div>}
      <svg viewBox={`0 0 ${VW} ${VH}`} width="100%" style={{ display: "block" }} role="img" aria-label="Site plan: the slabs on the lot">
        {footprints.map((r: any, i: number) => <polygon key={"f" + i} points={path(r)} fill="rgba(141,153,168,0.08)" stroke="#8d99a8" strokeWidth="1.2" strokeDasharray="5 4" />)}
        {p.house && p.house.length >= 3 && (() => { const c = ctr(p.house); return <text x={c[0]} y={c[1] + 4} textAnchor="middle" fontSize="11" fontFamily="monospace" fill="#8d99a8">house</text>; })()}
        {p.slabs.map((s: any, i: number) => <polygon key={"s" + i} points={path(s.poly)} fill="rgba(45,212,191,0.2)" stroke="#0fb39a" strokeWidth="2" strokeLinejoin="round" />)}
        {p.slabs.map((s: any, i: number) => {
          const c = ctr(s.poly); const label = `${kindLabel(s.kind)} · ${num0(s.sqft)} sq ft`; const wpx = label.length * 6.6 + 14;
          return (
            <g key={"l" + i}>
              <rect x={c[0] - wpx / 2} y={c[1] - 9} width={wpx} height="18" rx="4" fill="#0b2b27" />
              <text x={c[0]} y={c[1] + 4} textAnchor="middle" fontSize="11" fontFamily="monospace" fill="#2dd4bf">{label}</text>
            </g>
          );
        })}
        <g transform={`translate(${VW - 34}, 30)`}><polygon points="0,-14 6,6 0,2 -6,6" fill="#7c879a" /><text y="22" textAnchor="middle" fontSize="10" fontFamily="monospace" fill="#7c879a">N</text></g>
        <g transform={`translate(16, ${VH - 16})`}><line x1="0" y1="0" x2={barPx} y2="0" stroke="#7c879a" strokeWidth="2" /><line x1="0" y1="-4" x2="0" y2="4" stroke="#7c879a" strokeWidth="2" /><line x1={barPx} y1="-4" x2={barPx} y2="4" stroke="#7c879a" strokeWidth="2" /><text x={barPx / 2} y="-6" textAnchor="middle" fontSize="10" fontFamily="monospace" fill="#7c879a">20 ft</text></g>
      </svg>
    </div>
  );
}

/* the plan block on the contractor's quote page: from the quote's own inputs */
export function ConcretePlanBlock({ concrete }: any) {
  const plan = planOf(concrete);
  if (!plan) return null;
  return <div className="siteplan-block"><div className="kicker" style={{ marginBottom: 8 }}>Site plan</div><ConcreteSitePlan plan={plan} /></div>;
}
